import { Tooltip } from '@mui/material'
import React, { memo, useLayoutEffect, useRef, useState } from 'react'
import tw from 'twin.macro'
import { Chip } from 'src/components/shared/Chip'

/** Horizontal slack in `ch` for chip padding and non-monospace glyphs. */
const CHIP_WIDTH_PAD_CH = 4
const MIN_CHIP_MAX_WIDTH_CH = 8
const MAX_CHIP_MAX_WIDTH_CH = 72

interface Props {
  text: string
}

const LimitedStringChipComponent = ({ text }: Props) => {
  const textRef = useRef<HTMLElement>(null)

  const [isTruncated, setIsTruncated] = useState(false)

  useLayoutEffect(() => {
    const el = textRef.current

    if (!el) {
      return
    }

    const measure = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth + 0.5)
    }

    measure()

    let ro: ResizeObserver | undefined

    if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
      ro = new ResizeObserver(measure)
      ro.observe(el)
    }

    return () => {
      ro?.disconnect()
    }
  }, [text])

  const maxWidthCh = Math.min(
    Math.max(text.length + CHIP_WIDTH_PAD_CH, MIN_CHIP_MAX_WIDTH_CH),
    MAX_CHIP_MAX_WIDTH_CH,
  )

  const rootCss = tw`flex w-full min-w-0 items-center`
  const innerCss = [tw`min-w-0 max-w-full py-1`, { maxWidth: `min(100%, ${maxWidthCh}ch)` }]
  const chipClassName = tw`w-full min-w-0`
  const textClassName = tw`block w-full min-w-0 truncate`

  return (
    <Tooltip
      title={text}
      disableHoverListener={!isTruncated}
      disableFocusListener={!isTruncated}
      disableTouchListener={!isTruncated}
    >
      <div css={rootCss} {...(isTruncated ? { 'aria-label': text } : {})}>
        <div css={innerCss}>
          <Chip
            text={text}
            className={chipClassName}
            textRef={textRef}
            textClassName={textClassName}
          />
        </div>
      </div>
    </Tooltip>
  )
}

export const LimitedStringChip = memo(LimitedStringChipComponent)
